/**
 * pf-form.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Handles all Phonefinder lead form submissions.
 *
 * Flow:
 *   1. User submits form
 *   2. Validate visible fields (firstname, surname, id_number, email, cellphone)
 *   3. Populate geo fields via ipapi.co
 *   4. Build payload from all hidden + visible fields
 *   5. POST to correct LeadByte endpoint (determined by form's data-vertical)
 *   6. Show success or error state
 *
 * Requirements:
 *   - pf-tracking.js must load before this file (window.pfTracking available)
 *   - pf-webhooks.liquid must be rendered (window.pfWebhooks available)
 *   - Form must have data-vertical="phones" (or loans, debt, insurance)
 *   - pf-tracking-fields.liquid must be included inside the form
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /* ── Constants ── */
  var GEO_API      = 'https://ipapi.co/json/';
  var FORM_SELECTOR = '.pf-lead-form';

  /* ── Validation rules ── */
  var SA_PHONE_RE = /^(\+27|0)[6-8][0-9]{8}$/;
  var SA_ID_RE    = /^[0-9]{13}$/;
  var EMAIL_RE    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function validateForm(form) {
    var errors = [];
    var firstname = form.querySelector('[name="firstname"]');
    var surname   = form.querySelector('[name="surname"]');
    var idNumber  = form.querySelector('[name="id_number"]');
    var email     = form.querySelector('[name="email"]');
    var cellphone = form.querySelector('[name="cellphone_number"]');

    clearErrors(form);

    if (!firstname || !firstname.value.trim()) {
      errors.push({ el: firstname, msg: 'Please enter your first name' });
    }
    if (!surname || !surname.value.trim()) {
      errors.push({ el: surname, msg: 'Please enter your surname' });
    }
    if (!idNumber || !SA_ID_RE.test(idNumber.value.trim())) {
      errors.push({ el: idNumber, msg: 'Please enter a valid 13-digit SA ID number' });
    }
    if (!email || !EMAIL_RE.test(email.value.trim())) {
      errors.push({ el: email, msg: 'Please enter a valid email address' });
    }
    if (!cellphone || !SA_PHONE_RE.test(cellphone.value.trim().replace(/\s/g, ''))) {
      errors.push({ el: cellphone, msg: 'Please enter a valid SA cellphone number' });
    }

    errors.forEach(function (err) {
      showFieldError(err.el, err.msg);
    });

    return errors.length === 0;
  }

  function showFieldError(el, msg) {
    if (!el) return;
    el.classList.add('pf-field-error');
    var hint = document.createElement('span');
    hint.className = 'pf-field-error-msg';
    hint.textContent = msg;
    el.parentNode.appendChild(hint);
  }

  function clearErrors(form) {
    form.querySelectorAll('.pf-field-error').forEach(function (el) {
      el.classList.remove('pf-field-error');
    });
    form.querySelectorAll('.pf-field-error-msg').forEach(function (el) {
      el.remove();
    });
  }

  /* ── Geo lookup ── */
  function getGeo() {
    return fetch(GEO_API)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        return {
          country:   data.country_name  || '',
          ipaddress: data.ip            || '',
          towncity:  data.city          || '',
          county:    data.region        || '',
          postcode:  data.postal        || '',
          latitude:  data.latitude      ? String(data.latitude)  : '',
          longitude: data.longitude     ? String(data.longitude) : ''
        };
      })
      .catch(function () {
        /* Geo failure is non-blocking — return empty object */
        return {};
      });
  }

  /* ── Populate geo hidden fields ── */
  function applyGeo(form, geo) {
    var geoMap = {
      'pf-field-user-agent': navigator.userAgent || '',
      'pf-field-country':    geo.country   || '',
      'pf-field-ipaddress':  geo.ipaddress || '',
      'pf-field-city':       geo.towncity  || '',
      'pf-field-region':     geo.county    || '',
      'pf-field-postcode':   geo.postcode  || '',
      'pf-field-latitude':   geo.latitude  || '',
      'pf-field-longitude':  geo.longitude || ''
    };
    Object.keys(geoMap).forEach(function (id) {
      var el = form.querySelector('#' + id);
      if (el) el.value = geoMap[id];
    });

    /* Also update window.pfTracking for reference */
    Object.assign(window.pfTracking, geo);
  }

  /* ── Build payload from all form fields ── */
  function buildPayload(form, vertical) {
    var config = (window.pfWebhooks && window.pfWebhooks[vertical]) || {};

    /* Start with LeadByte core static fields */
    var payload = {
      sid:         config.sid         || '4001',
      suppier_key: config.suppierKey  || '',
      campid:      config.campid      || '',
      tenant_name: 'phonefinder',
      channel:     'Web'
    };

    /* Collect every named input in the form */
    var inputs = form.querySelectorAll('input[name], select[name], textarea[name]');
    inputs.forEach(function (input) {
      var name = input.name;
      var value = input.value.trim();
      if (name && value) {
        payload[name] = value;
      }
    });

    /* Ensure ssid fallback */
    if (!payload.ssid || payload.ssid === '') {
      payload.ssid = 'ORGANIC';
    }

    /* Remove internal-only fields that LeadByte doesn't accept */
    delete payload.suppier_key_display; /* safety — not a real field */

    return payload;
  }

  /* ── POST to LeadByte ── */
  function postToLeadByte(vertical, payload) {
    var config = (window.pfWebhooks && window.pfWebhooks[vertical]) || {};
    var endpoint = config.endpoint;

    if (!endpoint) {
      console.error('[pf-form] No endpoint configured for vertical:', vertical);
      return Promise.reject(new Error('No endpoint'));
    }

    return fetch(endpoint, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload)
    })
    .then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  /* ── UI state helpers ── */
  function setSubmitting(form, isSubmitting) {
    var btn = form.querySelector('[type="submit"]');
    if (!btn) return;
    btn.disabled = isSubmitting;
    btn.textContent = isSubmitting ? 'Submitting...' : 'Apply Now';
  }

  function showSuccess(form) {
    var successEl = form.querySelector('.pf-form-success');
    var fieldsEl  = form.querySelector('.pf-form-fields');
    if (fieldsEl)  fieldsEl.style.display  = 'none';
    if (successEl) successEl.style.display = 'block';
  }

  function showError(form, message) {
    var errorEl = form.querySelector('.pf-form-submit-error');
    if (errorEl) {
      errorEl.textContent = message || 'Something went wrong. Please try again.';
      errorEl.style.display = 'block';
    }
    setSubmitting(form, false);
  }

  /* ── Main submit handler ── */
  function handleSubmit(e) {
    e.preventDefault();
    var form     = e.currentTarget;
    var vertical = form.dataset.vertical || 'phones';

    /* 1. Validate */
    if (!validateForm(form)) return;

    setSubmitting(form, true);

    /* 2. Geo + submit */
    Promise.resolve(pfGeoCache || getGeo())
      .then(function (geo) {
        pfGeoCache = geo;
        applyGeo(form, geo);
        return buildPayload(form, vertical);
      })
      .then(function (payload) {
        return postToLeadByte(vertical, payload);
      })
      .then(function (response) {
        console.log('[pf-form] LeadByte response:', response);
        showSuccess(form);
      })
      .catch(function (err) {
        console.error('[pf-form] Submission error:', err);
        showError(form, 'Something went wrong. Please try again.');
      });
  }

  /* ── Bind to all lead forms on the page ── */
  function bindForms() {
    document.querySelectorAll(FORM_SELECTOR).forEach(function (form) {
      form.removeEventListener('submit', handleSubmit); /* prevent double-bind */
      form.addEventListener('submit', handleSubmit);
    });
  }

  /* ── Init ── */
  document.addEventListener('DOMContentLoaded', bindForms);

  /* Re-bind after drawer opens (form may have been injected after DOMContentLoaded) */
  document.addEventListener('pf:drawer:open', bindForms);

  /* Pre-fetch geo on drawer open so it's ready before submit */
  var pfGeoCache = null;
  document.addEventListener('pf:drawer:open', function () {
    if (pfGeoCache) return;
    getGeo().then(function (geo) { pfGeoCache = geo; });
  });

  /* Expose for manual re-binding after dynamic renders */
  window.pfForm = { bind: bindForms };

})();